import { useState } from 'react';
import { Briefcase, Calendar, Pencil, Trash2, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useRemoveExperience } from '../hooks';
import { ExperienceFormDialog } from './ExperienceFormDialog';
import type { CandidateExperience } from '../types';

interface ExperiencesSectionProps {
  candidateId: number;
  experiences: CandidateExperience[];
}

export function ExperiencesSection({ candidateId, experiences }: ExperiencesSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CandidateExperience | null>(null);
  const removeExperience = useRemoveExperience();

  const handleAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleEdit = (exp: CandidateExperience) => {
    setEditing(exp);
    setDialogOpen(true);
  };

  const handleDelete = async (exp: CandidateExperience) => {
    if (!confirm(`Supprimer l'expérience "${exp.title}" chez ${exp.company} ?`)) return;
    await removeExperience.mutateAsync({ candidateId, experienceId: exp.id });
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Expériences
              {experiences.length > 0 && (
                <span className="text-sm font-normal text-muted-foreground">
                  ({experiences.length})
                </span>
              )}
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleAdd} title="Ajouter une expérience">
              <Plus className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {experiences.length > 0 ? (
            <div className="space-y-3">
              {experiences.map((exp, idx) => (
                <div key={exp.id}>
                  {idx > 0 && <Separator className="my-3" />}
                  <div className="group">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{exp.title}</div>
                        <div className="text-sm text-muted-foreground">{exp.company}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Calendar className="h-3 w-3" />
                          {new Date(exp.startDate).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                          {' - '}
                          {exp.isCurrent
                            ? 'Présent'
                            : exp.endDate
                            ? new Date(exp.endDate).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
                            : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleEdit(exp)}
                          title="Modifier"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(exp)}
                          disabled={removeExperience.isPending}
                          title="Supprimer"
                        >
                          {removeExperience.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    </div>
                    {exp.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2 whitespace-pre-line">
                        {exp.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Button variant="ghost" size="sm" className="text-xs" onClick={handleAdd}>
              Ajouter une expérience
            </Button>
          )}
        </CardContent>
      </Card>

      <ExperienceFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        candidateId={candidateId}
        experience={editing}
      />
    </>
  );
}
